import { FC } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import { Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Tag } from '../../store/slices/tagsSlice';

import styles from './CustomMultiselect.module.scss';

type CustomMultiselectProps = {
  tags: Tag[] | null;
  tagsOptions: Tag[] | null;
  setTagsOptions: (tags: Tag[]) => void;
  placeholder?: string;
};

const CustomMultiselect: FC<CustomMultiselectProps> = ({
  tags,
  tagsOptions,
  setTagsOptions,
  placeholder = 'Tags...',
}) => {
  const hasSelection = (tagsOptions?.length ?? 0) > 0;
  const inputClassName = [styles.customInput, hasSelection ? styles.withTags : ''].filter(Boolean).join(' ');

  return (
    <Box id={styles.customSelectContainer}>
      <Autocomplete
        multiple
        disableClearable
        forcePopupIcon
        options={tags || []}
        getOptionLabel={option => option.name}
        value={tagsOptions || undefined}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        className={inputClassName}
        popupIcon={<ExpandMoreIcon />}
        onChange={(_, tags: Tag[]) => setTagsOptions(tags)}
        renderTags={(value: readonly Tag[], getTagProps) =>
          value.map((option: Tag, index: number) => {
            const tagProps = getTagProps({ index });
            const { key, ...rest } = tagProps;
            return (
              <Chip
                key={key}
                variant="filled"
                label={option.name}
                {...rest}
                deleteIcon={<CloseIcon sx={{ fontSize: 16, color: '#5E6A81' }} />}
                sx={{
                  height: 30,
                  minHeight: 30,
                  maxHeight: 30,
                  bgcolor: option.color || '#E0E0E0',
                  color: '#222B3A',
                  border: 'none',
                  borderRadius: '50px',
                  boxShadow: 'none',
                  flexShrink: 0,
                  '& .MuiChip-label': {
                    px: '10px',
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: '34px',
                  },
                  '& .MuiChip-deleteIcon': {
                    color: '#5E6A81',
                    backgroundColor: 'transparent',
                    borderRadius: 0,
                    width: 16,
                    height: 16,
                    '&:hover': {
                      color: '#5E6A81',
                      backgroundColor: 'transparent',
                    },
                  },
                  '& .MuiTouchRipple-root': {
                    display: 'none',
                  },
                }}
              />
            );
          })
        }
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            label=""
            placeholder={hasSelection ? '' : placeholder}
          />
        )}
        sx={{ width: '100%' }}
      />
    </Box>
  );
};

export default CustomMultiselect;
